import React from "react";
import strings from "../../core/translate";

/**
 * Compact pagination for the bottom of a table: how many rows per page, which
 * rows you are looking at, and first / previous / next / last.
 *
 * Drop-in for react-table v6 via its PaginationComponent prop.
 *
 * Tables that page on the server pass `serverPaged`, which react-table hands
 * through untouched. Only the rows of the current page are in hand then, so
 * the range counts from the page offset and drops the total — and "last page"
 * goes with it, since nothing here knows where the end is.
 */
export default class TablePagination extends React.Component {
  changePage(page) {
    const safe = Math.min(Math.max(page, 0), this.props.pages - 1);
    if (safe !== this.props.page) {
      this.props.onPageChange(safe);
    }
  }

  // "1 – 8 of 8", counting the rows actually loaded. Server-paged tables hold
  // one page at a time and no total, so they read "21 – 30".
  range() {
    const { page, pageSize, sortedData, data, serverPaged } = this.props;
    const rows = (sortedData || data || []).length;

    if (serverPaged) {
      if (rows === 0) return `0 ${strings.of} 0`;
      const from = page * pageSize + 1;
      return `${from} – ${page * pageSize + rows}`;
    }

    if (rows === 0) return `0 ${strings.of} 0`;
    const from = page * pageSize + 1;
    const to = Math.min((page + 1) * pageSize, rows);
    return `${from} – ${to} ${strings.of} ${rows}`;
  }

  render() {
    const {
      pages,
      page,
      pageSize,
      pageSizeOptions,
      showPageSizeOptions,
      canPrevious,
      canNext,
      onPageSizeChange,
      className,
      serverPaged,
    } = this.props;

    return (
      <div className={`${className || ""} -pagination table-pagination`}>
        {showPageSizeOptions && (
          <label className="table-pagination__size">
            <span>{strings.items_per_page}</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {(pageSizeOptions || [10, 20, 40, 60]).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}

        <span className="table-pagination__range">{this.range()}</span>

        <div className="table-pagination__controls">
          <button
            type="button"
            title={strings.first_page}
            aria-label={strings.first_page}
            disabled={!canPrevious}
            onClick={() => this.changePage(0)}
          >
            <i className="fa fa-angle-double-left" />
          </button>
          <button
            type="button"
            title={strings.previous}
            aria-label={strings.previous}
            disabled={!canPrevious}
            onClick={() => this.changePage(page - 1)}
          >
            <i className="fa fa-angle-left" />
          </button>
          <button
            type="button"
            title={strings.next}
            aria-label={strings.next}
            disabled={!canNext}
            onClick={() => this.changePage(page + 1)}
          >
            <i className="fa fa-angle-right" />
          </button>
          {!serverPaged && (
            <button
              type="button"
              title={strings.last_page}
              aria-label={strings.last_page}
              disabled={!canNext}
              onClick={() => this.changePage(pages - 1)}
            >
              <i className="fa fa-angle-double-right" />
            </button>
          )}
        </div>
      </div>
    );
  }
}
